import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CURATED_SHOPS from '@/mocks/cureatedShops';

const FILTERS = [
  { key: 'all', label: '전체' },
  { key: '클래식', label: '클래식' },
  { key: '트렌디', label: '트렌디' },
  { key: '체형보정', label: '체형보정' },
  { key: '호텔예식', label: '호텔예식' },
  { key: '가성비', label: '가성비' },
  { key: '원단강점', label: '원단강점' },
];

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const cardVariants = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

export default function YangbokListPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredShops = useMemo(() => {
    if (activeFilter === 'all') return CURATED_SHOPS;
    return CURATED_SHOPS.filter((shop) => shop.tags.includes(activeFilter));
  }, [activeFilter]);

  return (
    <div className="min-h-[100dvh] bg-cream pb-10">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-cream/90 backdrop-blur-md border-b border-cream-dark">
        <div className="flex items-center h-14 px-4 md:px-6 lg:px-8 max-w-[1200px] mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center text-navy cursor-pointer active:scale-90 transition-transform"
            aria-label="뒤로가기"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <h1 className="ml-2 text-sm font-semibold text-navy">예복 업체 탐색</h1>
        </div>
      </div>

      {/* Hero / Intro */}
      <div className="px-4 md:px-6 lg:px-8 pt-8 pb-6 max-w-[1200px] mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-serif text-xl md:text-2xl text-navy leading-relaxed mb-2"
        >
          다이렉트 웨딩이 엄선한
          <br />
          <span className="text-softgold">예복 업체들을 만나보세요</span>
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="text-xs md:text-sm text-navy-light leading-relaxed"
        >
          브랜드 분위기를 보고 취향에 맞는 예복샵을 발견해보세요
        </motion.p>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="px-4 md:px-6 lg:px-8 pb-6 max-w-[1200px] mx-auto"
      >
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {FILTERS.map((f) => {
            const isActive = activeFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-navy text-white shadow-md shadow-navy/10'
                    : 'bg-white text-navy-light border border-cream-dark hover:border-navy/20'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Card List */}
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="px-4 md:px-6 lg:px-8 max-w-[1200px] mx-auto"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {filteredShops.map((shop, index) => (
            <motion.article
              key={index}
              variants={cardVariants}
              onClick={() => navigate(`/yangbok/detail/test`)}
              className="group cursor-pointer active:scale-[0.98] transition-transform duration-200"
            >
              {/* Main image */}
              <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden mb-4 shadow-[0_2px_16px_rgba(26,39,68,0.06)]">
                <img
                  src={shop.image}
                  alt={shop.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/15 via-transparent to-transparent" />
              </div>

              {/* Text area */}
              <div className="px-0.5">
                <div className="flex items-baseline justify-between mb-1.5">
                  <h3 className="text-base md:text-lg font-semibold text-navy tracking-tight">
                    {shop.name}
                  </h3>
                  <span className="text-[11px] text-navy-light">{shop.area}</span>
                </div>
                <p className="text-xs md:text-sm text-navy-light leading-relaxed mb-3">
                  {shop.subtitle}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {shop.tags.slice(0, 4).map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] md:text-xs px-2.5 py-1 rounded-full bg-navy/5 text-navy-light font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Review count */}
                <p className="text-[11px] md:text-xs text-navy-light">
                  실제 후기 <span className="font-semibold text-navy">{shop.reviews}</span>개
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </motion.div>

      {/* Empty state */}
      {filteredShops.length === 0 && (
        <div className="px-4 md:px-6 lg:px-8 py-16 text-center max-w-[1200px] mx-auto">
          <p className="text-sm text-navy-light">해당 조건에 맞는 업체가 없습니다</p>
          <button
            onClick={() => setActiveFilter('all')}
            className="mt-3 text-xs text-navy font-medium underline underline-offset-2 cursor-pointer"
          >
            전체 업체 보기
          </button>
        </div>
      )}
    </div>
  );
}